import {
    Customer,
    Cart,
    Totals,
    ShippingMethod,
    PaymentMethod,
    CheckoutStep,
    Coupon,
    StoreLocator,
    QuoteAddress,
    CmsBlock,
    Agreement,
    CountryData,
    FidelityCard,
    GiftCard,
    PickupPoint,
} from '@/store/models';
import { cloneDeep, merge, get as dataGetter } from 'lodash';
import addressNormalizer, { getDefaultCustomAttributes } from '@/commons/addressNormalizer';
import { setJWT } from '@/store/api';
import { clone } from 'lodash';
import { i18n } from '@/i18n';

// tutorial: inizializziamo tutte le proprietà dello state con i loro default altrimenti non sono reactive
// qui entra in gioco anche lo stato iniziale che viene iniettato in pagina da php nell'app di produzione

const INITIAL_STATE = window.__INITIAL_STATE__ || {};

const customer: Customer = {
    id: 0,
    group_id: 0,
    default_billing: '',
    default_shipping: '',
    email: '',
    firstname: '',
    lastname: '',
    addresses: [],
    extension_attributes: {
        fidelity: '',
        hipay_tokenized_cards: [],
    },
};

const coupon: Coupon = {
    coupon: '',
    applied: false,
    error: '',
};

const selectedPayment = '';

const shippingMethods: ShippingMethod[] = [];

const paymentMethods: PaymentMethod[] = [];

const selectedShipment = '';

const address: QuoteAddress = {
    id:               0,
    region:           '',
    region_id:        0,
    country_id:       dataGetter(INITIAL_STATE, 'settings.country.default', 'IT'),
    street:           '',
    company:          '',
    telephone:        '',
    postcode:         '',
    city:             '',
    firstname:        '',
    lastname:         '',
    default_shipping: false,
    default_billing:  false,
    same_as_billing:  0,
    save_in_address_book: 0,
    custom_attributes: getDefaultCustomAttributes(),
};

const steps: CheckoutStep[] = [
    {
        name: 'userdata',
        label: i18n.t('data').toString(),
        active: true,
        unlocked: true,
        fulfilled: false,
    },
    {
        name: 'invoice',
        label: i18n.t('invoice').toString(),
        active: false,
        unlocked: false,
        fulfilled: false,
    },
    {
        name: 'shipping',
        label: i18n.t('shipping').toString(),
        active: false,
        unlocked: false,
        fulfilled: false,
    },
    {
        name: 'payment',
        label: i18n.t('Payment').toString(),
        active: false,
        unlocked: false,
        fulfilled: false,
    },
    {
        name: 'review',
        label: i18n.t('review').toString(),
        active: false,
        unlocked: false,
        fulfilled: false,
    },
];

const quoteAddress = clone(address) as QuoteAddress;
quoteAddress.save_in_address_book = 0;
quoteAddress.same_as_billing = 0;

const billingAddress: QuoteAddress = addressNormalizer(quoteAddress) as QuoteAddress;
billingAddress.extension_attributes = {
    invoicerequest: {
        enabled: false,
        cfpiva: '',
        dateofbirth: '',
        gender: '',
        placeofbirth: '',
    },
};

const shippingAddress: QuoteAddress = addressNormalizer(quoteAddress) as QuoteAddress;

const checkoutEmail = '';

const cart: Cart = {
    id: 0,
    is_active: true,
    is_virtual: false,
    items: [],
    items_count: 0,
    items_qty: 0,
    orig_order_id: 0,
    currency: {
        global_currency_code: '',
        base_currency_code: '',
        store_currency_code: '',
        quote_currency_code: '',
        store_to_base_rate: 0,
        store_to_quote_rate: 0,
        base_to_global_rate: 0,
        base_to_quote_rate: 0,
    },
    customer_is_guest: true,
    customer_tax_class_id: 0,
    store_id: 0,
    billing_address: undefined,
    shipping_address: undefined,
};

const totals: Totals = {
    grand_total:                   0,
    base_grand_total:              0,
    subtotal:                      0,
    base_subtotal:                 0,
    discount_amount:               0,
    base_discount_amount:          0,
    subtotal_with_discount:        0,
    base_subtotal_with_discount:   0,
    shipping_amount:               0,
    base_shipping_amount:          0,
    shipping_discount_amount:      0,
    base_shipping_discount_amount: 0,
    tax_amount:                    0,
    base_tax_amount:               0,
    weee_tax_applied_amount:       0,
    shipping_tax_amount:           0,
    base_shipping_tax_amount:      0,
    subtotal_incl_tax:             0,
    shipping_incl_tax:             0,
    base_shipping_incl_tax:        0,
    base_currency_code:            '',
    quote_currency_code:           '',
    items_qty:                     0,
    total_segments:                [],
    extension_attributes: {
        blackbox_response: {
            promotions: [],
            promo_threshold: [],
        }
    }
};

const fidelityCard: FidelityCard = {
    bonus: 0,
    end_date: '',
    max_usable: 0,
    applied_bonus: 0,
    use_bonus_enabled: false,
    loyalty_double_optin_using_point_disabled: false,
};

const giftCard: GiftCard = {
    code: '',
    applied: false,
    verified: false,
    credit: 0,
    applied_credit: 0,
    error: '',
};

const settings = JSON.parse(process.env.VUE_APP_STORE_OPTIONS);

const storelocators: StoreLocator[] = [];

const token: string = '';

const cartToken: string = '';

const cmsblocks: CmsBlock[] = [];

const agreements: Agreement[] = [];

const countrydata: CountryData = {
    countries: [],
    regions: [],
    associations: {},
};

const selectedStoreLocatorId = '';

const paymentSuccessInfo = {};

const cartbanners: string[] = [];

const selectedPickupPoint: PickupPoint = {id: '', data: {}}

const stateObj: any = {
    customer,
    cart,
    totals,
    address,
    coupon,
    selectedPayment,
    shippingMethods,
    paymentMethods,
    selectedShipment,
    billingAddress,
    shippingAddress,
    checkoutEmail,
    steps,
    storelocators,
    token,
    cartToken,
    cmsblocks,
    agreements,
    countrydata,
    settings,
    selectedStoreLocatorId,
    fidelityCard,
    giftCard,
    paymentSuccessInfo,
    cartbanners,
    selectedPickupPoint,
};

const clonedStateObj = cloneDeep(stateObj);

export function initState(key: string) {
    if (typeof INITIAL_STATE[key] === 'string') {
        if (key === 'token' && INITIAL_STATE[key] !== '') {
            setJWT(INITIAL_STATE[key]);
        }
        return INITIAL_STATE[key];
    }
    return merge(stateObj[key], INITIAL_STATE[key] || {});
}


export function blankState(key: string) {
    return clonedStateObj[key];
}
